/**
 * Centralized constants for ORCA360 application
 *
 * IMPORTANT: These constants must match database CHECK constraints exactly.
 * Migration 083 defines the flight_phase constraint with these exact values.
 */

/**
 * Flight phase options for session categorization
 *
 * These values represent segments of a flight from preflight to landing.
 * Used in:
 * - SessionForm dropdown
 * - EventDetectionPanel LLM-enhanced classification
 * - Database sessions.flight_phase column (CHECK constraint)
 *
 * @see supabase/migrations/083_replace_training_phase_with_flight_phase.sql
 */
export const FLIGHT_PHASE_OPTIONS = [
  'Preflight',
  'Taxi',
  'Takeoff',
  'Climb',
  'Cruise',
  'Descent',
  'Approach',
  'Landing',
  'Go-Around',
  'Full Flight'
] as const;

/**
 * TypeScript type for flight phase values
 * Derived from FLIGHT_PHASE_OPTIONS constant
 */
export type FlightPhase = typeof FLIGHT_PHASE_OPTIONS[number];

/**
 * Default flight phase for new sessions
 */
export const DEFAULT_FLIGHT_PHASE: FlightPhase = 'Full Flight';

/**
 * Global airline ID
 */
export const GLOBAL_AIRLINE_ID = '00000000-0000-0000-0000-000000000000';

/**
 * Cockpit language options for code switching (AssemblyAI Universal-3-Pro).
 * English is always the primary language; these are the secondary language options.
 *
 * Group 1: U3 Pro native — single-pass transcription
 * Group 2: Non-native — triggers two-pass pipeline (U3 Pro speakers + U2 text)
 *
 * KEEP IN SYNC with packages/shared/src/config/assemblyai.ts COCKPIT_LANGUAGE_OPTIONS
 */
export const COCKPIT_LANGUAGE_OPTIONS = [
  { code: null, label: 'None (English only)' },

  // U3 Pro native languages (single-pass)
  { code: 'es', label: 'Spanish' },
  { code: 'fr', label: 'French' },
  { code: 'de', label: 'German' },
  { code: 'it', label: 'Italian' },
  { code: 'pt', label: 'Portuguese' },

  // Non-native languages (two-pass pipeline)
  { code: 'nl', label: 'Dutch' },
  { code: 'ar', label: 'Arabic' },
  { code: 'hi', label: 'Hindi' },
  { code: 'zh', label: 'Mandarin' },
  { code: 'ja', label: 'Japanese' },
  { code: 'ko', label: 'Korean' },
  { code: 'id', label: 'Indonesian' },
  { code: 'ms', label: 'Malay' },
  { code: 'th', label: 'Thai' },
  { code: 'vi', label: 'Vietnamese' },
  { code: 'ru', label: 'Russian' },
  { code: 'tr', label: 'Turkish' },
  { code: 'pl', label: 'Polish' },
  { code: 'sv', label: 'Swedish' },
  { code: 'no', label: 'Norwegian' },
  { code: 'da', label: 'Danish' },
  { code: 'fi', label: 'Finnish' },
  { code: 'el', label: 'Greek' },
  { code: 'ro', label: 'Romanian' },
  { code: 'hu', label: 'Hungarian' },
  { code: 'cs', label: 'Czech' },
] as const;